// The six reachable corners, each a ratio of the 40px bezel anchor — an
// explicit table of design decisions, not a `base * 0.7^n` chain. A rung
// sits near that geometric curve by coincidence; a formula would make a
// later per-rung adjustment (a designer moving one step on its own)
// impossible to express.

export type RadiusRung = 'xs' | 'lg' | 'xl' | '2xl' | '3xl' | 'bezel'

export const RADIUS_RUNGS: readonly RadiusRung[] = ['xs', 'lg', 'xl', '2xl', '3xl', 'bezel']

// Two shapes sit outside the scalar ladder: a pill and a circle are geometry,
// not a length, so neither rescales when the bezel anchor moves. They are named
// here anyway because the library previously spelled "fully round" three ways —
// 9999px, 999px and 50% — and three spellings of one shape is the same
// divergence the ladder exists to end. `full` matches Tailwind's own
// `rounded-full` so a class and a declaration render identically.
export type RadiusShape = 'full' | 'circle'

export const RADIUS_SHAPE_NAMES: readonly RadiusShape[] = ['full', 'circle']

export const RADIUS_SHAPES: Readonly<Record<RadiusShape, string>> = {
	full: 'calc(infinity * 1px)',
	circle: '50%',
}

// One vocabulary at the call site: a component prop that selects a corner
// holds either family, and which one it holds decides nothing the caller
// cares about — only whether the base is a parameter at all, which is the
// ladder's own business.
export type RadiusToken = RadiusRung | RadiusShape

export function isRadiusShape(token: RadiusToken): token is RadiusShape {
	return token in RADIUS_SHAPES
}

// F1 — pending Step 1 (the designer proving 6/7/8px candidates against the
// ladder in the studio). 0.175 (7px @ the 40px bezel) is the placeholder
// until that step lands — one named constant so the swap stays a one-line
// edit instead of a value spread across the table below.
const XS_RATIO_PENDING_STEP_1 = 0.175

const RADIUS_RATIOS: Record<RadiusRung, number> = {
	xs: XS_RATIO_PENDING_STEP_1,
	lg: 0.25,
	xl: 0.35,
	'2xl': 0.5,
	'3xl': 0.7,
	bezel: 1,
}

// Every sibling token (theme.style.css, mixins/tailwind.css) emits rem off
// the browser's fixed 16px root — the ladder matches that convention.
const PX_PER_REM = 16

export function rungPx(rung: RadiusRung, basePx: number): number {
	return basePx * RADIUS_RATIOS[rung]
}

export function rungRem(rung: RadiusRung, basePx: number): string {
	return `${rungPx(rung, basePx) / PX_PER_REM}rem`
}
